import { supabase } from "@/integrations/supabase/client";
import type { ServiceType } from "@/lib/services";

export const REVIEW_SORTS = ["latest", "highest", "lowest"] as const;
export type ReviewSort = (typeof REVIEW_SORTS)[number];

export const REVIEW_SORT_LABEL: Record<ReviewSort, string> = {
  latest: "Latest",
  highest: "Highest Rating",
  lowest: "Lowest Rating",
};

export interface ListingReview {
  id: string;
  listing_id: string;
  listing_kind: string;
  service_type: ServiceType;
  listing_title: string | null;
  partner_id: string;
  customer_id: string;
  customer_name: string | null;
  rating: number;
  title: string | null;
  body: string | null;
  partner_reply: string | null;
  replied_at: string | null;
  is_hidden: boolean;
  hidden_reason: string | null;
  report_count: number;
  created_at: string;
  updated_at: string;
}

export interface ReviewSummary {
  average: number;
  total: number;
  breakdown: Record<1 | 2 | 3 | 4 | 5, number>;
}

export function summarize(reviews: ListingReview[]): ReviewSummary {
  const breakdown = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 } as ReviewSummary["breakdown"];
  let sum = 0;
  for (const r of reviews) {
    const key = Math.min(5, Math.max(1, Math.round(r.rating))) as 1 | 2 | 3 | 4 | 5;
    breakdown[key] += 1;
    sum += r.rating;
  }
  return {
    average: reviews.length ? Number((sum / reviews.length).toFixed(1)) : 0,
    total: reviews.length,
    breakdown,
  };
}

export function sortReviews(reviews: ListingReview[], sort: ReviewSort): ListingReview[] {
  const list = [...reviews];
  if (sort === "highest") {
    list.sort((a, b) => b.rating - a.rating || +new Date(b.created_at) - +new Date(a.created_at));
  } else if (sort === "lowest") {
    list.sort((a, b) => a.rating - b.rating || +new Date(b.created_at) - +new Date(a.created_at));
  } else {
    list.sort((a, b) => +new Date(b.created_at) - +new Date(a.created_at));
  }
  return list;
}

const TABLE = "listing_reviews";

/** Public reviews for one listing (RLS hides moderated ones from everyone but the author/partner/admin). */
export async function listListingReviews(listingId: string): Promise<ListingReview[]> {
  const { data, error } = await supabase
    .from(TABLE)
    .select("*")
    .eq("listing_id", listingId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data as unknown as ListingReview[]) ?? [];
}

export async function listPartnerReviews(partnerId: string): Promise<ListingReview[]> {
  const { data, error } = await supabase
    .from(TABLE)
    .select("*")
    .eq("partner_id", partnerId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data as unknown as ListingReview[]) ?? [];
}

export async function listCustomerReviews(customerId: string): Promise<ListingReview[]> {
  const { data, error } = await supabase
    .from(TABLE)
    .select("*")
    .eq("customer_id", customerId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data as unknown as ListingReview[]) ?? [];
}

export async function listAllReviews(): Promise<ListingReview[]> {
  const { data, error } = await supabase
    .from(TABLE)
    .select("*")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data as unknown as ListingReview[]) ?? [];
}

/**
 * A review may only be written by a customer whose enquiry was actually
 * engaged with by the partner (read/replied/closed) — mirrors the
 * has_verified_enquiry() database check behind the insert policy.
 */
export async function hasVerifiedEnquiry(customerId: string, listingId: string): Promise<boolean> {
  const { count, error } = await supabase
    .from("partner_enquiries")
    .select("id", { count: "exact", head: true })
    .eq("customer_id", customerId)
    .eq("listing_id", listingId)
    .in("enquiry_status", ["read", "replied", "closed"]);
  if (error) throw error;
  return (count ?? 0) > 0;
}


export interface NewReviewInput {
  listingId: string;
  listingKind: string;
  serviceType: ServiceType;
  listingTitle: string | null;
  partnerId: string;
  customerId: string;
  customerName: string | null;
  rating: number;
  title: string;
  body: string;
}

export async function submitReview(input: NewReviewInput) {
  const { error } = await supabase.from(TABLE).insert({
    listing_id: input.listingId,
    listing_kind: input.listingKind,
    service_type: input.serviceType,
    listing_title: input.listingTitle,
    partner_id: input.partnerId,
    customer_id: input.customerId,
    customer_name: input.customerName,
    rating: input.rating,
    title: input.title.trim() || null,
    body: input.body.trim() || null,
  } as never);
  if (error) throw error;
}

export async function updateOwnReview(
  id: string,
  patch: { rating: number; title: string; body: string },
) {
  const { error } = await supabase
    .from(TABLE)
    .update({
      rating: patch.rating,
      title: patch.title.trim() || null,
      body: patch.body.trim() || null,
    } as never)
    .eq("id", id);
  if (error) throw error;
}

export async function deleteReview(id: string) {
  const { error } = await supabase.from(TABLE).delete().eq("id", id);
  if (error) throw error;
}

export async function replyToReview(id: string, reply: string) {
  const { error } = await supabase
    .from(TABLE)
    .update({ partner_reply: reply.trim(), replied_at: new Date().toISOString() } as never)
    .eq("id", id);
  if (error) throw error;
}

export async function setReviewHidden(
  id: string,
  hidden: boolean,
  reason: string,
  adminId: string,
) {
  const { error } = await supabase
    .from(TABLE)
    .update({
      is_hidden: hidden,
      hidden_reason: hidden ? reason.trim() || "Inappropriate content" : null,
      hidden_by: hidden ? adminId : null,
      hidden_at: hidden ? new Date().toISOString() : null,
    } as never)
    .eq("id", id);
  if (error) throw error;
}

export async function reportReview(reviewId: string, reporterId: string, reason: string) {
  const { error } = await supabase.from("review_reports").insert({
    review_id: reviewId,
    reporter_id: reporterId,
    reason: reason.trim() || "Reported by user",
  } as never);
  if (error && !`${error.message}`.includes("duplicate")) throw error;
}
